/**
 * Technician view, job screen (tracker item 42): customer and address links,
 * notes and work items (no prices), a live Start / Stop timer and Finish
 * (phase 2); tick-only checklist, photos (the shared attachments section with
 * the phone camera), stamped tech notes and the optional customer signature
 * (phase 3). The card's checklist progress follows the ticks, so the Finish
 * warning is always current.
 */

import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import axios from 'axios';
import { createAPIClient, INotificationsApi, ITechnicianApi } from '../api';
import { TechnicianOnMyWayButton } from '../components/TechnicianOnMyWayButton';
import * as types from '../api/types';
import { JobEnrollmentChip } from '../features/service-bundles/EnrollmentChip';
import { useAuth } from '../context/AuthContext';
import { useNow } from '../components/useNow';
import { AttachmentsSection } from '../components/AttachmentsSection';
import { inputClass, primaryButtonClass, secondaryButtonClass } from '../components/formStyles';
import { getErrorCode, getErrorMessage } from '../utils/apiError';
import { canTickJobChecklist } from '../utils/permissions';
import {
  arrivalWindowLabel,
  canStartStatus,
  checklistProgressLabel,
  elapsedSeconds,
  formatAddress,
  formatElapsed,
  isDoneStatus,
  mapsUrl,
  signatureErrorMessage,
  techNoteError,
  telHref,
  timeWindowLabel,
} from '../utils/technician';
import { JobChecklistApi, JobChecklistSection } from './JobChecklistSection';
import { TechnicianSignatureHandle, TechnicianSignatureSection } from './TechnicianSignatureSection';
import { TechnicianStatusBadge } from './technicianUi';

const bigButton = 'flex w-full min-h-[56px] items-center justify-center rounded-lg px-4 text-base font-bold text-white disabled:opacity-50 disabled:cursor-not-allowed';
const cardClass = 'rounded-lg bg-white p-4 shadow-sm ring-1 ring-gray-200';
const linkButton = 'inline-flex min-h-[44px] items-center rounded-md px-3 text-sm font-semibold text-blue-700 ring-1 ring-blue-200 hover:bg-blue-50';

/** What the 409 handlers ask the technician before anything is resent. */
type Prompt =
  | { kind: 'offSchedule'; scheduledDate: string | null; today: string | null }
  | { kind: 'elsewhere'; jobId: string | null; confirmOffSchedule: boolean };

function errorDetails<T>(err: unknown): T | undefined {
  if (axios.isAxiosError(err)) {
    const data = err.response?.data as { details?: unknown } | undefined;
    if (data && typeof data.details === 'object' && data.details !== null) return data.details as T;
  }
  return undefined;
}

function statusOf(err: unknown): number | undefined {
  return axios.isAxiosError(err) ? err.response?.status : undefined;
}

export function TechnicianJobPage() {
  const { id = '' } = useParams<{ id: string }>();
  const { user } = useAuth();
  const api = useMemo(() => createAPIClient() as ITechnicianApi & JobChecklistApi & Partial<Pick<INotificationsApi, 'getJobCustomerNotifications' | 'sendJobCustomerNotification'>>, []);
  const [card, setCard] = useState<types.TechnicianJobCard | null>(null);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState<{ message: string; status?: number } | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [timeZone, setTimeZone] = useState<string | undefined>(undefined);
  const [prompt, setPrompt] = useState<Prompt | null>(null);
  const [confirmFinish, setConfirmFinish] = useState(false);
  const [finishWarning, setFinishWarning] = useState<number | null>(null);
  const [noteText, setNoteText] = useState('');
  const [noteError, setNoteError] = useState<string | null>(null);
  const [signatureInk, setSignatureInk] = useState(false);
  const signatureRef = useRef<TechnicianSignatureHandle>(null);
  const inFlight = useRef(false);
  const requestId = useRef(0);

  const load = useCallback(async () => {
    const reqId = ++requestId.current;
    setLoading(true);
    setLoadError(null);
    try {
      const result = await api.getTechnicianJob(id);
      if (reqId !== requestId.current) return;
      setCard(result);
    } catch (err) {
      if (reqId !== requestId.current) return;
      setCard(null);
      setLoadError({ message: getErrorMessage(err, 'Could not load this job.'), status: statusOf(err) });
    } finally {
      if (reqId === requestId.current) setLoading(false);
    }
  }, [api, id]);

  // Company timezone for the signed-at time; any staff role may read the settings. Best effort.
  useEffect(() => {
    let cancelled = false;
    Promise.resolve()
      .then(() => (api as Partial<typeof api> & { getCompanySettings?: () => Promise<types.CompanySettings> }).getCompanySettings?.())
      .then((settings) => { if (!cancelled && settings?.timezone) setTimeZone(settings.timezone); })
      .catch(() => undefined);
    return () => { cancelled = true; };
  }, [api]);

  useEffect(() => {
    setCard(null);
    setPrompt(null);
    setError(null);
    setNotice(null);
    setConfirmFinish(false);
    setFinishWarning(null);
    setNoteText('');
    setNoteError(null);
    void load();
  }, [load]);

  // What the checklist section needs of the card; stable so it does not reload on every tick.
  const cardId = card?.id;
  const cardAssignedTo = card?.assignedTo ?? null;
  const checklistJob = useMemo(() => (cardId ? { id: cardId, assignedTo: cardAssignedTo } : null), [cardId, cardAssignedTo]);

  /** Keeps the card's progress (and the Finish warning) in step with the ticks. */
  const onChecklistProgress = useCallback((progress: types.JobChecklistProgress) => {
    setCard((prev) => {
      if (!prev) return prev;
      const same = prev.checklist.total === progress.total
        && prev.checklist.completed === progress.completed
        && prev.checklist.requiredIncomplete === progress.requiredIncomplete;
      return same ? prev : { ...prev, checklist: progress };
    });
  }, []);

  const myEntry = card && user ? card.openTimeEntries.find((e) => e.userId === user.id) ?? null : null;
  const now = useNow(!!myEntry);

  /** Runs one action: one request at a time, buttons disabled meanwhile, state cleared on failure. */
  const run = async (action: () => Promise<void>) => {
    if (inFlight.current) return;
    inFlight.current = true;
    setBusy(true);
    setError(null);
    setNotice(null);
    try {
      await action();
    } finally {
      inFlight.current = false;
      setBusy(false);
    }
  };

  const applyState = (state: types.TechnicianJobState, open: (prev: types.TechnicianJobCard['openTimeEntries']) => types.TechnicianJobCard['openTimeEntries']) => {
    setCard((prev) => (prev ? { ...prev, status: state.status, completedDate: state.completedDate, openTimeEntries: open(prev.openTimeEntries) } : prev));
  };

  const startJob = async (confirmOffSchedule: boolean, stopOtherFirst?: string | null) => {
    await run(async () => {
      try {
        if (stopOtherFirst) {
          try {
            await api.stopTechnicianJob(stopOtherFirst);
          } catch (err) {
            // The other timer already ended: carry on with the start.
            if (getErrorCode(err) !== 'NO_RUNNING_TIMER') throw err;
          }
        }
        const result = await api.startTechnicianJob(id, confirmOffSchedule ? { confirmOffSchedule: true } : undefined);
        setPrompt(null);
        applyState(result.job, (prev) => [...prev.filter((e) => e.id !== result.entry.id), { id: result.entry.id, userId: result.entry.userId, startedAt: result.entry.startedAt }]);
        if (result.alreadyRunning) setNotice('The timer was already running.');
      } catch (err) {
        const code = getErrorCode(err);
        if (code === 'NOT_SCHEDULED_TODAY') {
          const details = errorDetails<types.NotScheduledTodayDetails>(err);
          setPrompt({ kind: 'offSchedule', scheduledDate: details?.scheduledDate ?? null, today: details?.today ?? null });
        } else if (code === 'TIMER_RUNNING_ELSEWHERE') {
          const details = errorDetails<types.TimerRunningElsewhereDetails>(err);
          setPrompt({ kind: 'elsewhere', jobId: details?.jobId ?? null, confirmOffSchedule });
        } else {
          setPrompt(null);
          setError(getErrorMessage(err, 'Could not start the timer.'));
        }
      }
    });
  };

  const stopJob = async () => {
    await run(async () => {
      try {
        const result = await api.stopTechnicianJob(id);
        applyState(result.job, (prev) => prev.filter((e) => e.id !== result.entry.id));
      } catch (err) {
        if (getErrorCode(err) === 'NO_RUNNING_TIMER') {
          setNotice('The timer was not running.');
          await load();
        } else {
          setError(getErrorMessage(err, 'Could not stop the timer.'));
        }
      }
    });
  };

  const finishJob = async () => {
    await run(async () => {
      // The drawn signature (optional) travels with Finish; a half-filled one stops here, nothing is sent.
      const collected = await (signatureRef.current?.collect() ?? Promise.resolve({ ok: true as const, signature: null }));
      if (!collected.ok) {
        setError(collected.message);
        return;
      }
      setConfirmFinish(false);
      try {
        const result = await api.finishTechnicianJob(id, collected.signature ? { signature: collected.signature } : undefined);
        applyState(result.job, () => []);
        setFinishWarning(result.requiredIncomplete);
        signatureRef.current?.reload();
      } catch (err) {
        const code = getErrorCode(err);
        if (code === 'JOB_ALREADY_COMPLETED') {
          setNotice('This job is already completed.');
          await load();
        } else {
          setError(signatureErrorMessage(code) ?? getErrorMessage(err, 'Could not finish the job.'));
        }
      }
    });
  };

  const addNote = async () => {
    const issue = techNoteError(noteText);
    if (issue) {
      setNoteError(issue);
      return;
    }
    await run(async () => {
      setNoteError(null);
      try {
        const result = await api.addTechnicianNote(id, noteText);
        setCard((prev) => (prev ? { ...prev, notes: result.notes } : prev));
        setNoteText('');
      } catch (err) {
        setNoteError(getErrorMessage(err, 'Could not add the note. Your text is still here; try again.'));
      }
    });
  };

  if (loading && !card) {
    return (
      <div className="flex justify-center py-10" role="status" aria-label="Loading job">
        <div className="h-10 w-10 animate-spin rounded-full border-b-2 border-blue-500" />
      </div>
    );
  }

  if (loadError || !card) {
    const message = loadError?.status === 403
      ? 'You can only open jobs assigned to you.'
      : loadError?.status === 404 ? 'Job not found.' : loadError?.message ?? 'Could not load this job.';
    return (
      <div role="alert" className="rounded-lg bg-red-50 p-4 text-sm text-red-800 ring-1 ring-red-200">
        <p>{message}</p>
        <div className="mt-3 flex gap-2">
          {loadError?.status !== 403 && loadError?.status !== 404 && (
            <button type="button" className={secondaryButtonClass} onClick={() => void load()}>Try again</button>
          )}
          <Link to="/tech" className={secondaryButtonClass + ' inline-flex items-center'}>Back to my jobs</Link>
        </div>
      </div>
    );
  }

  const done = isDoneStatus(card.status);
  const canStart = !done && canStartStatus(card.status);
  const canFinish = !done && (card.status === types.JobStatus.IN_PROGRESS || canStartStatus(card.status));
  const tel = telHref(card.customer.phone);
  const arrival = arrivalWindowLabel(card.arrivalWindowMinutes);
  const progress = checklistProgressLabel(card.checklist);
  const requiredLeft = card.checklist.requiredIncomplete;

  return (
    <div className="space-y-4">
      <section className={cardClass}>
        <div className="flex items-start justify-between gap-3">
          <h1 className="text-xl font-bold text-gray-900">{card.customer.name}</h1>
          <TechnicianStatusBadge card={card} userId={user?.id} />
        </div>
        <p className="mt-1 text-sm text-gray-600">
          {card.scheduledDate ? `${card.scheduledDate} - ` : ''}{timeWindowLabel(card)}
          {arrival ? ` (${arrival})` : ''}
        </p>
        {card.route && <p className="text-xs text-gray-500">Route: {card.route.name}</p>}
        {/* Service bundle visit (tracker item 85): read-only label, technicians cannot open enrollments */}
        <div className="mt-2"><JobEnrollmentChip job={card} linked={false} /></div>

        <div className="mt-4 flex flex-wrap gap-2">
          {tel ? (
            <a href={tel} className={linkButton} aria-label={`Call ${card.customer.name}`}>Call {card.customer.phone}</a>
          ) : (
            <span className="text-sm text-gray-500">No phone number</span>
          )}
        </div>

        <p className="mt-4 text-sm text-gray-900">{formatAddress(card.property)}</p>
        <a href={mapsUrl(card.property)} target="_blank" rel="noopener noreferrer" className={linkButton + ' mt-2'}>Open in Maps</a>
      </section>

      {/* Timer and actions */}
      <section className={cardClass} aria-label="Timer">
        {myEntry && (
          <div className="mb-3 text-center">
            <div className="text-xs font-semibold uppercase tracking-wide text-gray-500">Time on site</div>
            <div className="font-mono text-4xl font-bold text-gray-900" data-testid="elapsed" aria-live="off">
              {formatElapsed(elapsedSeconds(myEntry.startedAt, now))}
            </div>
          </div>
        )}

        {!done && !canStart && !canFinish && !myEntry && (
          <p className="text-center text-sm text-gray-600" role="status">
            {card.status === types.JobStatus.CANCELLED
              ? 'This job is cancelled.'
              : 'This job is not scheduled yet, so it cannot be started. Ask the office.'}
          </p>
        )}

        {done && (
          <p className="text-center text-sm font-semibold text-green-700" role="status">
            This job is completed{card.completedDate ? ` (${card.completedDate})` : ''}.
          </p>
        )}

        <div className="space-y-3">
          {/* Day-of message (tracker item 43): before Start, which means "on site" */}
          {!done && !myEntry && card.status !== types.JobStatus.IN_PROGRESS && (
            <TechnicianOnMyWayButton api={api} jobId={card.id} jobStatus={card.status} disabled={busy} />
          )}
          {canStart && !myEntry && (
            <button type="button" className={bigButton + ' bg-green-600 hover:bg-green-700'} disabled={busy} onClick={() => void startJob(false)}>
              Start
            </button>
          )}
          {myEntry && (
            <button type="button" className={bigButton + ' bg-yellow-600 hover:bg-yellow-700'} disabled={busy} onClick={() => void stopJob()}>
              Stop
            </button>
          )}
          {canFinish && !confirmFinish && (
            <button type="button" className={bigButton + ' bg-blue-600 hover:bg-blue-700'} disabled={busy} onClick={() => setConfirmFinish(true)}>
              Finish
            </button>
          )}
        </div>

        {confirmFinish && (
          <div className="mt-3 rounded-lg bg-blue-50 p-4 ring-1 ring-blue-200" role="group" aria-label="Confirm finish">
            <p className="text-sm text-blue-900">Finish this job? It will be marked completed and the timer stops.</p>
            {signatureInk && <p className="mt-2 text-sm text-blue-900">The customer signature will be saved with the job.</p>}
            {requiredLeft > 0 && (
              <p className="mt-2 text-sm font-semibold text-yellow-800" role="note">
                {requiredLeft} required checklist {requiredLeft === 1 ? 'step is' : 'steps are'} not ticked. You can still finish.
              </p>
            )}
            <div className="mt-3 flex gap-2">
              <button type="button" className={bigButton + ' bg-blue-600 hover:bg-blue-700'} disabled={busy} onClick={() => void finishJob()}>
                Finish job
              </button>
              <button type="button" className={secondaryButtonClass + ' min-h-[56px]'} disabled={busy} onClick={() => setConfirmFinish(false)}>
                Cancel
              </button>
            </div>
          </div>
        )}

        {prompt?.kind === 'offSchedule' && (
          <div className="mt-3 rounded-lg bg-yellow-50 p-4 ring-1 ring-yellow-300" role="alertdialog" aria-label="Not scheduled for today">
            <p className="text-sm text-yellow-900">
              {prompt.scheduledDate
                ? `This job is scheduled for ${prompt.scheduledDate}${prompt.today ? `, not today (${prompt.today})` : ''}.`
                : 'This job has no scheduled date.'}{' '}
              Start it anyway?
            </p>
            <div className="mt-3 flex gap-2">
              <button type="button" className={bigButton + ' bg-green-600 hover:bg-green-700'} disabled={busy} onClick={() => void startJob(true)}>
                Start anyway
              </button>
              <button type="button" className={secondaryButtonClass + ' min-h-[56px]'} disabled={busy} onClick={() => setPrompt(null)}>
                Cancel
              </button>
            </div>
          </div>
        )}

        {prompt?.kind === 'elsewhere' && (
          <div className="mt-3 rounded-lg bg-yellow-50 p-4 ring-1 ring-yellow-300" role="alertdialog" aria-label="Timer running on another job">
            <p className="text-sm text-yellow-900">You have a timer running on another job. Stop it to start this one.</p>
            {prompt.jobId && (
              <Link to={`/tech/jobs/${prompt.jobId}`} className={linkButton + ' mt-2'}>Open the other job</Link>
            )}
            <div className="mt-3 flex gap-2">
              {prompt.jobId && (
                <button type="button" className={bigButton + ' bg-yellow-600 hover:bg-yellow-700'} disabled={busy} onClick={() => void startJob(prompt.confirmOffSchedule, prompt.jobId)}>
                  Stop the other job
                </button>
              )}
              <button type="button" className={secondaryButtonClass + ' min-h-[56px]'} disabled={busy} onClick={() => setPrompt(null)}>
                Cancel
              </button>
            </div>
          </div>
        )}

        {error && (
          <p role="alert" className="mt-3 rounded-md bg-red-50 p-3 text-sm text-red-800 ring-1 ring-red-200">{error}</p>
        )}
        {notice && <p role="status" className="mt-3 rounded-md bg-gray-50 p-3 text-sm text-gray-700">{notice}</p>}
        {finishWarning !== null && (
          <div role="status" className="mt-3 rounded-md bg-green-50 p-3 text-sm text-green-900 ring-1 ring-green-200">
            <p className="font-semibold">Job finished.</p>
            {finishWarning > 0 && (
              <p role="alert" className="mt-1 text-yellow-800">
                {finishWarning} required checklist {finishWarning === 1 ? 'step was' : 'steps were'} not ticked.
              </p>
            )}
          </div>
        )}
      </section>

      <section className={cardClass} aria-label="Job notes">
        <h2 className="text-sm font-bold text-gray-900">Job notes</h2>
        {card.notes ? (
          <p className="mt-1 whitespace-pre-wrap break-words text-sm text-gray-700" data-testid="job-notes">{card.notes}</p>
        ) : (
          <p className="mt-1 text-sm text-gray-500">No notes.</p>
        )}
        <div className="mt-3 border-t border-gray-100 pt-3">
          <label htmlFor="tech-note" className="mb-1.5 block text-sm font-medium text-gray-700">Add a note</label>
          <textarea
            id="tech-note"
            rows={2}
            className={`${inputClass} min-h-[44px]`}
            value={noteText}
            maxLength={types.TECH_NOTE_MAX}
            disabled={busy}
            placeholder="What did you find or do?"
            onChange={(event) => { setNoteText(event.target.value); setNoteError(null); }}
          />
          <p className="mt-1 text-xs text-gray-500">Saved as one line with the time and your email. Notes cannot be edited afterwards.</p>
          {noteError && <p role="alert" className="mt-2 rounded-md bg-red-50 p-3 text-sm text-red-800 ring-1 ring-red-200">{noteError}</p>}
          <button
            type="button"
            className={`${primaryButtonClass} mt-2 min-h-[44px] w-full`}
            disabled={busy || noteText.trim() === ''}
            onClick={() => void addNote()}
          >
            Add note
          </button>
        </div>
      </section>

      <section className={cardClass} aria-label="Work items">
        <h2 className="text-sm font-bold text-gray-900">Work items</h2>
        {card.workItems.length === 0 ? (
          <p className="mt-1 text-sm text-gray-500">No work items.</p>
        ) : (
          <ul className="mt-2 divide-y divide-gray-100">
            {card.workItems.map((item) => (
              <li key={item.id} className="flex items-start justify-between gap-3 py-2 text-sm">
                <span>
                  <span className="font-medium text-gray-900">{item.name}</span>
                  {item.description && <span className="block text-xs text-gray-500">{item.description}</span>}
                </span>
                <span className="shrink-0 font-semibold text-gray-700">x {item.quantity}</span>
              </li>
            ))}
          </ul>
        )}
      </section>

      {checklistJob && canTickJobChecklist(user, checklistJob) ? (
        <JobChecklistSection api={api} job={checklistJob} user={user} onProgress={onChecklistProgress} tickOnly />
      ) : (
        <section className={cardClass} aria-label="Checklist">
          <h2 className="text-sm font-bold text-gray-900">Checklist</h2>
          {progress ? (
            <p className="mt-1 text-sm text-gray-700">
              {progress} done
              {requiredLeft > 0 && <span className="ml-2 font-semibold text-yellow-800">{requiredLeft} required left</span>}
            </p>
          ) : (
            <p className="mt-1 text-sm text-gray-500">No checklist steps.</p>
          )}
        </section>
      )}

      <AttachmentsSection
        parentType="JOB"
        parentId={card.id}
        user={user}
        sectionClassName={cardClass}
        headingClassName="text-sm font-bold text-gray-900"
      />

      <TechnicianSignatureSection
        ref={signatureRef}
        api={api}
        jobId={card.id}
        done={done}
        cancelled={card.status === types.JobStatus.CANCELLED}
        busy={busy}
        timeZone={timeZone}
        onInkChange={setSignatureInk}
      />
    </div>
  );
}
